import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center justify-center overflow-hidden"
      aria-label="Seção principal"
    >
      <div className="absolute inset-0">
        <img
          alt="Energia de cura e prosperidade — ondas violetas e douradas representando frequências sonoras de transformação"
          className="w-full h-full object-cover object-top"
          loading="eager"
          width={1800}
          height={900}
          src="https://readdy.ai/api/search-image?query=Abstract%20spiritual%20healing%20art%2C%20deep%20cosmic%20indigo%20and%20violet%20space%20with%20warm%20golden%20amber%20light%20waves%20radiating%20outward%2C%20shimmering%20copper%20particles%20and%20honey%20energy%20streams%20rising%20upward%2C%20soft%20ethereal%20glow%20with%20healing%20vibration%20energy%2C%20no%20text%20no%20people%2C%20dreamy%20atmospheric%20composition%20with%20luminous%20bokeh%20effects%2C%20high%20detail%20abstract%20background&width=1800&height=900&seq=hero-cura-v1&orientation=landscape"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55" />
      <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/20 border border-accent-400/30 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <i className="ri-sparkling-line text-accent-400" />
            <span className="text-xs md:text-sm font-medium text-accent-300 whitespace-nowrap">
              Você está aqui neste momento, pois essa é a luz que você queria!
            </span>
          </div>
          <h1
            className={`font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            3 áudios de cura para transformar
            <br />
            <span className="bg-gradient-to-r from-accent-400 via-accent-300 to-secondary-400 bg-clip-text text-transparent">
              sua mente, coração e prosperidade
            </span>
          </h1>
          <p
            className={`text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            Frequências sonoras cuidadosamente calibradas para curar sua criança interior,
            limpar relacionamentos passados e ativar a prosperidade em todas as áreas da sua vida.
            Pagamento único de R$ 49,90 — acesso por 1 ano.
          </p>
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <a
              href="https://pay.hotmart.com/T106583743Q?checkoutMode=2"
              onClick={(e) => e.preventDefault()}
              className="hotmart-fb hotmart__button-checkout inline-flex items-center whitespace-nowrap cursor-pointer"
            >
              <span className="inline-flex items-center px-8 py-4 rounded-full bg-accent-500 text-background-50 font-bold text-base md:text-lg shadow-lg shadow-accent-500/30 transition-all hover:bg-accent-600 hover:shadow-accent-500/40 hover:scale-105 focus-visible:outline-2 focus-visible:outline-accent-500">
                Quero meu pacote agora
                <i className="ri-arrow-right-line ml-2" />
              </span>
            </a>
            <a
              href="#audios"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#audios')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center whitespace-nowrap cursor-pointer px-6 py-4 rounded-full border border-background-300/40 text-background-100 font-medium text-sm md:text-base transition-all hover:bg-background-50/10 hover:border-background-200"
            >
              <i className="ri-play-circle-line mr-2 text-lg" />
              Conheça os 3 áudios
            </a>
          </div>

          <div
            className={`mt-10 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <p className="text-xs font-semibold text-background-300/60 uppercase tracking-[0.2em] mb-4 flex items-center justify-center gap-1.5">
              <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500/30">
                <i className="ri-headphone-line text-accent-300 text-[10px]" />
              </div>
              Ouça amostras grátis antes de comprar
            </p>
            <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
              {[
                { icon: 'ri-heart-pulse-line', title: 'Cura da Criança Interior', time: '28 min' },
                { icon: 'ri-links-line', title: 'Limpeza de Relacionamentos', time: '46 min' },
                { icon: 'ri-money-dollar-circle-line', title: 'Prosperidade e Abundância', time: '47 min' },
              ].map((item) => (
                <a
                  key={item.title}
                  href="#audios"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#audios')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-300/25 text-background-100 text-xs font-medium transition-all hover:bg-background-50/20 hover:border-accent-400/40 hover:scale-105 cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500/25">
                    <i className={`${item.icon} text-accent-300 text-sm`} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">{item.title}</div>
                    <div className="text-[10px] text-background-300/50">{item.time}</div>
                  </div>
                  <i className="ri-play-fill ml-1" />
                </a>
              ))}
            </div>
          </div>

          <div
            className={`flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-10 text-background-300/80 text-xs md:text-sm transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-shield-check-line text-accent-400" />
              Pagamento Seguro
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-headphone-line text-accent-400" />
              Acesso Imediato
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-refresh-line text-accent-400" />
              7 Dias de Garantia
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-bank-card-line text-accent-400" />
              Pix ou Cartão
            </span>
          </div>

          <div
            className={`mt-6 transition-all duration-700 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-50/15 border border-accent-400/40 backdrop-blur-sm">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 shadow-sm">
                <i className="ri-calendar-check-line text-background-50 text-sm" />
              </div>
              <span className="text-sm font-bold text-background-50 whitespace-nowrap">Acesso por 1 ano</span>
              <span className="hidden sm:inline text-xs text-background-200/80">ouça online quando quiser</span>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent" />
    </section>
  );
}